import { useMemo } from 'react';
import TrendChart from './charts/TrendChart';
import { useColorScheme } from '../hooks/useColorScheme';
import { COLORS } from '../utils/colors';

/**
 * Persuadable Share card body — replaces the Commitment/Rejection Trendline
 * cards for allocation questions (gated by the same show_ci_ri_trend flag).
 *
 * Persuadable = 100 − committed − rejecting, computed from the SAME
 * cohorts-trend data the old trendline cards fetched — no new API calls.
 *
 * Props:
 *   commitmentTrend — { dates, series: [{ name: option, data: [...] }] }
 *   rejectionTrend  — same shape
 *   option          — which question option to display
 */

const round1 = (v) => (v == null || isNaN(v) ? null : Math.round(v * 10) / 10);

export function buildPersuadableSeries(commitmentTrend, rejectionTrend, option) {
  const dates = commitmentTrend?.dates || [];
  const ci = commitmentTrend?.series?.find((s) => s.name === option);
  const ri = rejectionTrend?.series?.find((s) => s.name === option);
  if (!ci || !ri || dates.length === 0) return null;

  const committed = dates.map((_, i) => round1(ci.data?.[i]));
  const rejecting = dates.map((_, i) => round1(ri.data?.[i]));
  const persuadable = dates.map((_, i) => {
    const c = ci.data?.[i], r = ri.data?.[i];
    if (c == null || r == null) return null;
    return round1(Math.max(0, 100 - c - r));
  });
  return { dates, committed, rejecting, persuadable };
}

function formatMonth(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function PersuadableShare({ commitmentTrend, rejectionTrend, option }) {
  const { colors } = useColorScheme();

  const built = useMemo(
    () => buildPersuadableSeries(commitmentTrend, rejectionTrend, option),
    [commitmentTrend, rejectionTrend, option]
  );

  if (!built) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
        No cohort trend data available for this option.
      </div>
    );
  }

  const { dates, committed, rejecting, persuadable } = built;
  const n = dates.length;
  const cur = persuadable[n - 1];
  const curCommitted = committed[n - 1];
  const curRejecting = rejecting[n - 1];

  // Delta vs same month last year when available, else vs series start
  const hasYear = n > 12 && persuadable[n - 13] != null;
  const prev = hasYear ? persuadable[n - 13] : persuadable[0];
  const delta = cur != null && prev != null ? Math.round((cur - prev) * 10) / 10 : null;
  const deltaLabel = hasYear ? 'vs last year' : `since ${formatMonth(dates[0])}`;

  const parts = [
    { name: 'Committed', value: curCommitted, color: colors.teal },
    { name: 'Persuadable', value: cur, color: colors.persuadable },
    { name: 'Rejecting', value: curRejecting, color: colors.coral }
  ];

  const trendData = {
    dates,
    series: [
      { name: 'Committed', data: committed },
      { name: 'Persuadable', data: persuadable },
      { name: 'Rejecting', data: rejecting }
    ]
  };

  return (
    <div style={gridStyle}>
      {/* Left: hero number + composition bar */}
      <div>
        <div style={labelStyle}>Persuadable · {option} · {formatMonth(dates[n - 1])}</div>
        <div style={heroStyle}>{cur != null ? cur.toFixed(1) + '%' : '—'}</div>
        {delta != null && (
          <div style={subStyle}>
            <span style={{ fontWeight: 700, color: delta >= 0 ? '#0f7c6e' : '#b8511f' }}>
              {(delta > 0 ? '+' : delta < 0 ? '−' : '') + Math.abs(delta).toFixed(1)} pts
            </span>{' '}
            {deltaLabel}
          </div>
        )}
        <div style={{ marginTop: '18px' }}>
          <div style={stackStyle}>
            {parts.map((p, i) => (
              <div
                key={p.name}
                title={`${p.name}: ${p.value != null ? p.value.toFixed(1) : '—'}%`}
                style={{
                  flexGrow: Math.max(0.5, p.value || 0),
                  flexBasis: 0,
                  background: p.color,
                  borderRadius: i === 0 ? '4px 0 0 4px' : i === parts.length - 1 ? '0 4px 4px 0' : 0
                }}
              />
            ))}
          </div>
          <div style={stackLabelsStyle}>
            {parts.map((p) => (
              <span key={p.name}>
                <strong style={{ color: COLORS.textPrimary }}>
                  {p.value != null ? p.value.toFixed(1) + '%' : '—'}
                </strong>{' '}
                {p.name.toLowerCase()}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right: 3-line trend */}
      <div>
        <div style={legendStyle}>
          {parts.map((p) => (
            <span key={p.name} style={legendItemStyle}>
              <span style={{ ...legendDotStyle, background: p.color }} />
              <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>{p.name}</span>
            </span>
          ))}
        </div>
        <div style={{ height: '200px' }}>
          <TrendChart
            data={trendData}
            colors={[colors.teal, colors.persuadable, colors.coral]}
          />
        </div>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const gridStyle = {
  display: 'grid', gridTemplateColumns: '280px 1fr', gap: '26px', alignItems: 'start'
};

const labelStyle = {
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
  color: '#8a94a3', marginBottom: '6px'
};

const heroStyle = {
  fontSize: '44px', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1
};

const subStyle = { fontSize: '12.5px', color: '#5a6577', marginTop: '6px' };

const stackStyle = {
  display: 'flex', gap: '2px', height: '22px', width: '100%'
};

const stackLabelsStyle = {
  display: 'flex', justifyContent: 'space-between', fontSize: '12px',
  color: '#5a6577', marginTop: '7px', gap: '8px', flexWrap: 'wrap'
};

const legendStyle = { display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' };
const legendItemStyle = { display: 'flex', alignItems: 'center', gap: '8px' };
const legendDotStyle = { width: '12px', height: '12px', borderRadius: '3px', flexShrink: 0 };
